import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../stores/languageStore';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
  showResultsInfo?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [6, 12, 24, 48],
  showPageSizeSelector = true,
  showResultsInfo = true,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  // Generate page numbers to show
  const getPageNumbers = () => {
    const delta = 2; // Number of pages to show on each side of current page
    const range = [];
    const rangeWithDots = [];

    // Always include first page
    range.push(1);

    // Add pages around current page
    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    // Always include last page if there are multiple pages
    if (totalPages > 1) {
      range.push(totalPages);
    }

    // Remove duplicates and sort
    const uniqueRange = [...new Set(range)].sort((a, b) => a - b);

    // Add dots where there are gaps
    let prev = 0;
    for (const page of uniqueRange) {
      if (page - prev > 1) {
        rangeWithDots.push('...');
      }
      rangeWithDots.push(page);
      prev = page;
    }

    return rangeWithDots;
  };

  const pageNumbers = getPageNumbers();

  if (totalPages <= 1 && !showResultsInfo && !showPageSizeSelector) {
    return null;
  }

  return (
    <div className={`flex flex-col lg:flex-row items-center justify-between gap-4 mt-6 ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Results info */}
      {showResultsInfo && (
        <div className="text-sm text-gray-700 order-2 lg:order-1">
          {totalItems > 0 ? (
            t('pagination.showing', {
              start: startItem,
              end: endItem,
              total: totalItems,
              defaultValue: `Showing ${startItem} to ${endItem} of ${totalItems} results`
            })
          ) : (
            t('pagination.noResults', { defaultValue: 'No results found' })
          )}
        </div>
      )}

      {/* Page navigation */}
      {totalPages > 1 && (
        <nav className="flex items-center space-x-1 order-1 lg:order-2" aria-label="Pagination">
          {/* Previous button */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={t('pagination.previous', { defaultValue: 'Previous' })}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "m9 5 7 7-7 7" : "m15 19-7-7 7-7"} />
            </svg>
          </button>

          {/* Page numbers */}
          {pageNumbers.map((page, index) => {
            if (page === '...') {
              return (
                <span key={`dots-${index}`} className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700">
                  ...
                </span>
              );
            }

            const pageNum = page as number;
            const isCurrentPage = pageNum === currentPage;

            return (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                className={`relative inline-flex items-center px-3 py-2 text-sm font-medium border rounded-md ${
                  isCurrentPage
                    ? 'z-10 bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
                aria-current={isCurrentPage ? 'page' : undefined}
              >
                {pageNum}
              </button>
            );
          })}

          {/* Next button */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={t('pagination.next', { defaultValue: 'Next' })}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "m15 19-7-7 7-7" : "m9 5 7 7-7 7"} />
            </svg>
          </button>
        </nav>
      )}

      {/* Page size selector */}
      {showPageSizeSelector && (
        <div className="flex items-center space-x-2 order-3">
          <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap">
            {t('pagination.perPage', { defaultValue: 'Per page:' })}
          </label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="border border-gray-300 rounded-md px-3 py-1 text-sm bg-white min-w-[70px]"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default Pagination;